import React, { useState, useEffect, useRef, useCallback } from "react";
import { resumeApi } from "../../services/api";
import { useToast } from "./Toast";

interface LangState {
  /** Blob URL for the iframe preview */
  previewUrl: string;
  /** Whether a file exists on the server */
  exists: boolean;
  /** Upload in progress */
  uploading: boolean;
  /** Loading preview from server */
  loadingPreview: boolean;
}

const EMPTY: LangState = {
  previewUrl: "",
  exists: false,
  uploading: false,
  loadingPreview: false,
};

const CvPage: React.FC = () => {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [pt, setPt] = useState<LangState>({ ...EMPTY });
  const [en, setEn] = useState<LangState>({ ...EMPTY });
  const ptInputRef = useRef<HTMLInputElement>(null);
  const enInputRef = useRef<HTMLInputElement>(null);

  /* ── helpers ─────────────────────────────────────────────── */

  const revoke = (url: string) => {
    if (url) URL.revokeObjectURL(url);
  };

  const loadPreview = useCallback(
    async (
      lang: "pt-br" | "en-us",
      setter: React.Dispatch<React.SetStateAction<LangState>>
    ) => {
      setter((prev) => ({ ...prev, loadingPreview: true }));
      const blob = await resumeApi.downloadBlob(lang);
      if (blob && blob.size > 0) {
        const url = URL.createObjectURL(blob);
        setter((prev) => {
          revoke(prev.previewUrl);
          return { ...prev, previewUrl: url, exists: true, loadingPreview: false };
        });
      } else {
        setter((prev) => {
          revoke(prev.previewUrl);
          return { ...prev, previewUrl: "", exists: false, loadingPreview: false };
        });
      }
    },
    []
  );

  /* ── initial load ────────────────────────────────────────── */

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      try {
        const info = await resumeApi.getInfo().catch(() => null);

        const hasPt = !!(info?.success && info.data?.urlPtBr);
        const hasEn = !!(info?.success && info.data?.urlEnUs);

        const promises: Promise<void>[] = [];
        if (hasPt) promises.push(loadPreview("pt-br", setPt));
        if (hasEn) promises.push(loadPreview("en-us", setEn));
        await Promise.all(promises);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    void init();
  }, [loadPreview]);

  /* Cleanup blob URLs on unmount */
  useEffect(() => {
    return () => {
      revoke(pt.previewUrl);
      revoke(en.previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── upload ──────────────────────────────────────────────── */

  const handleUpload = async (
    lang: "pt-br" | "en-us",
    file: File,
    setter: React.Dispatch<React.SetStateAction<LangState>>
  ) => {
    setter((prev) => ({ ...prev, uploading: true }));

    // Show local preview immediately
    const localUrl = URL.createObjectURL(file);
    setter((prev) => {
      revoke(prev.previewUrl);
      return { ...prev, previewUrl: localUrl };
    });

    try {
      const response =
        lang === "pt-br"
          ? await resumeApi.upload(file, null)
          : await resumeApi.upload(null, file);

      if (response.success) {
        setter((prev) => ({ ...prev, exists: true, uploading: false }));
        showToast(
          `Currículo (${lang === "pt-br" ? "PT-BR" : "EN-US"}) enviado com sucesso!`
        );
        // Re-fetch from server so preview reflects what was actually saved
        await loadPreview(lang, setter);
      } else {
        showToast(response.message || "Falha ao enviar currículo", "error");
        setter((prev) => ({ ...prev, uploading: false }));
      }
    } catch {
      showToast("Ocorreu um erro ao enviar", "error");
      setter((prev) => ({ ...prev, uploading: false }));
    }
  };

  /* ── file picker ─────────────────────────────────────────── */

  const onFileChange =
    (
      lang: "pt-br" | "en-us",
      setter: React.Dispatch<React.SetStateAction<LangState>>,
    ) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.type !== "application/pdf") {
        showToast("Apenas arquivos PDF são aceitos", "error");
        e.target.value = "";
        return;
      }

      handleUpload(lang, file, setter);
      e.target.value = "";
    };

  /* ── drag & drop ─────────────────────────────────────────── */

  const handleDrop =
    (
      lang: "pt-br" | "en-us",
      setter: React.Dispatch<React.SetStateAction<LangState>>,
    ) =>
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      if (file.type !== "application/pdf") {
        showToast("Apenas arquivos PDF são aceitos", "error");
        return;
      }
      handleUpload(lang, file, setter);
    };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /* ── loading state ───────────────────────────────────────── */

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  /* ── render helpers ──────────────────────────────────────── */

  const renderUploadArea = (
    lang: "pt-br" | "en-us",
    state: LangState,
    inputRef: React.RefObject<HTMLInputElement | null>,
    setter: React.Dispatch<React.SetStateAction<LangState>>
  ) => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "2.5rem 1.5rem",
        border: "2px dashed rgba(255,255,255,0.15)",
        borderRadius: "0.75rem",
        cursor: "pointer",
        transition: "border-color 0.2s, background 0.2s",
      }}
      onClick={() => inputRef.current?.click()}
      onDrop={handleDrop(lang, setter)}
      onDragOver={handleDragOver}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)";
        e.currentTarget.style.background = "rgba(255,255,255,0.02)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
        e.currentTarget.style.background = "transparent";
      }}
    >
      {state.uploading ? (
        <>
          <i
            className="fa-solid fa-spinner fa-spin"
            style={{ fontSize: "2.5rem", marginBottom: "0.75rem", color: "var(--color-text-secondary, #aaa)" }}
          />
          <span style={{ color: "var(--color-text-secondary, #aaa)" }}>Enviando...</span>
        </>
      ) : (
        <>
          <i
            className="fa-solid fa-cloud-arrow-up"
            style={{ fontSize: "2.5rem", marginBottom: "0.75rem", color: "var(--color-text-secondary, #aaa)" }}
          />
          <span style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
            Clique ou arraste um PDF aqui
          </span>
          <span style={{ fontSize: "0.8rem", color: "var(--color-text-secondary, #aaa)" }}>
            Apenas arquivos .pdf são aceitos
          </span>
        </>
      )}
    </div>
  );

  const renderCard = (
    lang: "pt-br" | "en-us",
    label: string,
    state: LangState,
    inputRef: React.RefObject<HTMLInputElement | null>,
    setter: React.Dispatch<React.SetStateAction<LangState>>
  ) => (
    <div className="admin-card" style={{ marginBottom: "1.5rem" }}>
      <div className="admin-card-header">
        <span className="admin-card-title">
          <i
            className="fa-solid fa-file-pdf"
            style={{ marginRight: "0.5rem", color: "#e74c3c" }}
          />
          {label}
        </span>

        {state.exists && (
          <div className="admin-cv-actions" style={{ display: "flex", gap: "0.5rem" }}>
            <a
              href={resumeApi.getDownloadUrl(lang)}
              target="_blank"
              rel="noopener noreferrer"
              className="admin-btn admin-btn-secondary admin-btn-sm"
              title="Baixar"
            >
              <i className="fa-solid fa-download" />
              <span>Baixar</span>
            </a>
            <button
              className="admin-btn admin-btn-primary admin-btn-sm"
              onClick={() => inputRef.current?.click()}
              disabled={state.uploading}
              title="Substituir"
            >
              <i
                className={`fa-solid ${
                  state.uploading
                    ? "fa-spinner fa-spin"
                    : "fa-arrow-up-from-bracket"
                }`}
              />
              <span>{state.uploading ? "Enviando..." : "Substituir"}</span>
            </button>
          </div>
        )}
      </div>

      <div style={{ padding: "1.5rem" }}>
        {/* PDF preview */}
        {state.loadingPreview && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "500px",
              background: "rgba(255,255,255,0.03)",
              borderRadius: "0.5rem",
              marginBottom: "1rem",
            }}
          >
            <i
              className="fa-solid fa-spinner fa-spin"
              style={{ fontSize: "2rem", color: "var(--color-text-secondary, #aaa)" }}
            />
          </div>
        )}

        {!state.loadingPreview && state.previewUrl && (
          <div
            className="admin-cv-preview"
            style={{
              borderRadius: "0.5rem",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
              marginBottom: "1rem",
              background: "#fff",
            }}
          >
            <iframe
              src={state.previewUrl}
              title={`Preview ${label}`}
              style={{
                width: "100%",
                height: "600px",
                border: "none",
                display: "block",
              }}
            />
          </div>
        )}

        {/* Upload area (show when no file exists, or as a small dropzone below preview) */}
        {!state.exists && !state.loadingPreview && (
          renderUploadArea(lang, state, inputRef, setter)
        )}

        <input
          ref={inputRef}
          type="file"
          accept="application/pdf"
          style={{ display: "none" }}
          onChange={onFileChange(lang, setter)}
        />
      </div>
    </div>
  );

  /* ── main render ─────────────────────────────────────────── */

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Currículo</h1>
        <p className="admin-page-subtitle">
          Faça upload do seu currículo em Português e Inglês. O PDF será
          exibido em tempo real abaixo.
        </p>
      </div>

      {renderCard("pt-br", "Currículo — Português (PT-BR)", pt, ptInputRef, setPt)}
      {renderCard("en-us", "Currículo — English (EN-US)", en, enInputRef, setEn)}
    </div>
  );
};

export default CvPage;
